import React, { useCallback, useRef } from "react";
import AsyncSelect from "react-select/async";
import { Typography, Box, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

function SupplierSelect({ value, onChange, loadOptions, placeholder = "Select supplier...", isLoading }) {
  const lastErrorRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const [inputValue, setInputValue] = React.useState('');

  // ✅ Debounced load options to prevent excessive API calls
  const debouncedLoadOptions = useCallback(
    (inputValue, callback) => {
      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const options = await loadOptions(inputValue);
          callback(options);
          lastErrorRef.current = null; // Reset error on success
        } catch (error) {
          // ✅ Only show toast if error message changed (prevent repetitive toasts)
          const errorMsg = error.message || 'Failed to search suppliers';
          if (lastErrorRef.current !== errorMsg) {
            lastErrorRef.current = errorMsg;
          }
          callback([]);
        }
      }, 500); // 500ms debounce delay
    },
    [loadOptions]
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={0.5}>
        Supplier
      </Typography>
      <Box sx={{ position: 'relative' }}>
        <AsyncSelect
          cacheOptions
          defaultOptions={false}
          value={value}
          onChange={(newValue) => {
            onChange(newValue);
            // Clear input when value is cleared via X button
            if (!newValue) {
              setInputValue('');
            }
          }}
          inputValue={inputValue}
          onInputChange={(newValue, actionMeta) => {
            // Update input value for all actions except when blurring or menu closing
            if (actionMeta.action !== 'input-blur' && actionMeta.action !== 'menu-close') {
              setInputValue(newValue);
            }
            // Clear input when clearing selection
            if (actionMeta.action === 'set-value' && !newValue) {
              setInputValue('');
            }
          }}
          loadOptions={debouncedLoadOptions}
          placeholder={placeholder}
          isClearable
          isLoading={isLoading}
          noOptionsMessage={({ inputValue }) =>
            !inputValue || inputValue.length < 3
              ? "Type at least 3 characters to search..."
              : "No suppliers found"
          }
          styles={{
            control: (base) => ({
              ...base,
              borderRadius: 8,
              minHeight: 40,
              fontSize: 15,
              borderColor: "#b0b6c7",
              boxShadow: "none",
            }),
            menu: (base) => ({
              ...base,
              borderRadius: 8,
              fontSize: 15,
              zIndex: 20,
            }),
          }}
          getOptionLabel={opt => opt.label || opt.Name}
          getOptionValue={opt => opt.value || opt.Id}
        />
        {inputValue && !value && (
          <IconButton
            size="small"
            onClick={() => setInputValue('')}
            sx={{
              position: 'absolute',
              right: 40,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              padding: '4px',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export default SupplierSelect;